import axios from 'axios';
import { viewAuthenticatedFile } from './authenticatedPdf';

export function getFileDownloadUrl(mode, entityId, resource, itemId) {
  const base = mode === 'onboarding'
    ? `/api/users/onboard/${entityId}`
    : `/api/users/${entityId}`;
  return `${base}/${resource}/${itemId}/download`;
}

export function getCertificateVersionDownloadUrl(mode, entityId, certId, versionId) {
  const base = mode === 'onboarding'
    ? `/api/users/onboard/${entityId}`
    : `/api/users/${entityId}`;
  return `${base}/certificates/${certId}/versions/${versionId}/download`;
}

async function messageFromErrorBlob(error) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      if (parsed?.message) return parsed.message;
      if (text) return text;
    } catch {
      // ignore
    }
  }
  return error?.response?.data?.message || error?.message || 'Failed to load file';
}

export async function previewUploadedFile({ mode, entityId, resource, itemId, mimeType }) {
  const url = getFileDownloadUrl(mode, entityId, resource, itemId);
  try {
    await viewAuthenticatedFile(url, mimeType);
  } catch (error) {
    const message = await messageFromErrorBlob(error);
    throw new Error(message);
  }
}

export async function downloadUploadedFile({ mode, entityId, resource, itemId, fileName, mimeType }) {
  const url = getFileDownloadUrl(mode, entityId, resource, itemId);
  try {
    const response = await axios.get(url, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });

    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      let message = 'Failed to download file';
      try {
        message = JSON.parse(text)?.message || message;
      } catch {
        if (text) message = text;
      }
      throw new Error(message);
    }

    const blobType = mimeType || contentType || 'application/octet-stream';
    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: blobType }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName || 'file');
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  } catch (error) {
    if (error?.message && !error?.response) throw error;
    throw new Error(await messageFromErrorBlob(error));
  }
}

export async function previewCertificateVersion({ mode, entityId, certId, versionId, mimeType }) {
  const url = getCertificateVersionDownloadUrl(mode, entityId, certId, versionId);
  try {
    await viewAuthenticatedFile(url, mimeType);
  } catch (error) {
    const message = await messageFromErrorBlob(error);
    throw new Error(message);
  }
}

export async function downloadCertificateVersion({
  mode,
  entityId,
  certId,
  versionId,
  fileName,
  mimeType
}) {
  const url = getCertificateVersionDownloadUrl(mode, entityId, certId, versionId);
  try {
    const response = await axios.get(url, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });

    const contentType = response.headers['content-type'] || '';
    if (contentType.includes('application/json')) {
      const text = await response.data.text();
      let message = 'Failed to download previous certificate';
      try {
        message = JSON.parse(text)?.message || message;
      } catch {
        if (text) message = text;
      }
      throw new Error(message);
    }

    const blobType = mimeType || contentType || 'application/octet-stream';
    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: blobType }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName || 'previous-certificate');
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  } catch (error) {
    if (error?.message && !error?.response) throw error;
    throw new Error(await messageFromErrorBlob(error));
  }
}
